import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, User, Send, X, ChevronDown, ChevronUp, Reply, Calendar, Search, Filter, Trash2, AlertTriangle, Plus, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

// Card de feedback individual dentro do histórico do aluno
const FeedbackItem = ({ feedback, onReply, onViewDetails, onDelete }) => {
  const hasReplies = feedback.reply_count > 0;
  const hasUnread = feedback.has_unread_reply;

  return (
    <div className={`border-l-2 pl-4 py-3 ${hasUnread ? 'border-neon-purple' : 'border-zinc-700'}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">
            {feedback.day_name}
          </span>
          {hasUnread && (
            <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-1 rounded ml-2">
              Nova resposta!
            </span>
          )}
        </div>
        <span className="text-xs text-zinc-500">
          {feedback.feedback_at && new Date(feedback.feedback_at).toLocaleDateString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
          })}
        </span>
      </div>
      
      <p className="text-zinc-300 text-sm bg-zinc-900/50 rounded-lg p-3 mb-2">{feedback.feedback}</p>
      
      <div className="flex items-center justify-between">
        <div>
          {hasReplies && (
            <span className="text-xs text-zinc-500">
              {feedback.reply_count} resposta(s)
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => onDelete(feedback)} 
            variant="ghost" 
            size="sm" 
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7"
            data-testid={`delete-feedback-${feedback.id}`}
          >
            <Trash2 className="w-3 h-3 mr-1" /> Excluir
          </Button>
          {hasReplies && (
            <Button onClick={() => onViewDetails(feedback)} variant="ghost" size="sm" className="text-zinc-400 hover:text-white text-xs h-7">
              Ver conversa
            </Button>
          )}
          <Button onClick={() => onReply(feedback)} size="sm" className="bg-neon-purple/20 text-neon-purple hover:bg-neon-purple/30 text-xs h-7">
            <Reply className="w-3 h-3 mr-1" /> Responder
          </Button>
        </div>
      </div>
    </div>
  );
};

// Item de mensagem direta (no card do aluno)
const DirectMessageItem = ({ message, onReply, onDelete }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasUnread = message.has_unread_reply || !message.read_by_admin;

  const handleSend = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      await onReply(message, replyText.trim());
      setReplyText('');
      setShowReply(false);
    } catch (e) {
      // handled in parent
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) return;
    setDeleting(true);
    try {
      await onDelete(message.id);
    } catch (e) {
      setDeleting(false);
    }
  };

  return (
    <div className={`border-l-2 pl-4 py-3 ${hasUnread ? 'border-neon-purple' : 'border-zinc-700'}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-1 rounded">
            {message.subject}
          </span>
          {hasUnread && (
            <span className="text-xs bg-neon-purple text-white px-2 py-0.5 rounded-full">Nova</span>
          )}
        </div>
        <span className="text-xs text-zinc-500">
          {new Date(message.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <p className="text-zinc-300 text-sm bg-zinc-900/50 rounded-lg p-3 mb-2">{message.message}</p>
      
      {/* Show existing replies */}
      {message.replies?.length > 0 && (
        <div className="space-y-1 mb-2 ml-2">
          {message.replies.map((r) => (
            <div key={r.id} className={`text-xs px-3 py-1.5 rounded ${r.author_type === 'admin' ? 'bg-neon-purple/10 text-neon-purple' : 'bg-zinc-800 text-zinc-400'}`}>
              <span className="font-bold">{r.author_type === 'admin' ? 'Você' : r.author_name}:</span> {r.message}
            </div>
          ))}
        </div>
      )}
      
      <div className="flex gap-2">
        <Button onClick={() => setShowReply(!showReply)} variant="ghost" size="sm" className="text-neon-purple hover:bg-neon-purple/10 h-7 text-xs">
          <Reply className="w-3 h-3 mr-1" /> Responder
        </Button>
        <Button onClick={handleDelete} disabled={deleting} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10 h-7 text-xs">
          <Trash2 className="w-3 h-3 mr-1" /> {deleting ? 'Excluindo...' : 'Excluir'}
        </Button>
      </div>
      
      {showReply && (
        <div className="flex gap-2 mt-2">
          <Input
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Responder..."
            className="flex-1 bg-zinc-900 border-zinc-800 text-white text-sm h-8"
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <Button onClick={handleSend} disabled={sending || !replyText.trim()} size="sm" className="bg-neon-purple text-white h-8">
            <Send className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};


// Card do aluno com seu histórico de feedbacks e mensagens
const StudentFeedbackCard = ({ student, feedbacks, messages = [], onReply, onViewDetails, onDeleteFeedback, onDeleteAllFeedbacks, onReplyToMessage, onDeleteMessage, isExpanded, onToggle }) => {
  const unreadFbCount = feedbacks.filter(f => f.has_unread_reply).length;
  const unreadMsgCount = messages.filter(m => m.has_unread_reply || !m.read_by_admin).length;
  const unreadCount = unreadFbCount + unreadMsgCount;
  const totalItems = feedbacks.length + messages.length;

  return (
    <motion.div
      className={`bg-zinc-950/50 border rounded-xl overflow-hidden ${unreadCount > 0 ? 'border-neon-purple/50' : 'border-white/5'}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Header do aluno - clicável para expandir */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-900/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${unreadCount > 0 ? 'bg-neon-purple/30 text-neon-purple' : 'bg-zinc-800 text-zinc-400'}`}>
            {student.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div>
            <h4 className="font-bold text-white flex items-center gap-2">
              {student.name}
              {unreadCount > 0 && (
                <span className="text-xs bg-neon-purple text-white px-2 py-0.5 rounded-full">
                  {unreadCount} nova(s)
                </span>
              )}
            </h4>
            <p className="text-xs text-zinc-500">{student.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-zinc-400">{totalItems} conversa(s)</p>
            <p className="text-xs text-zinc-600">
              {feedbacks.length > 0 && `${feedbacks.length} feedback(s)`}
              {feedbacks.length > 0 && messages.length > 0 && ' • '}
              {messages.length > 0 && `${messages.length} msg`}
            </p>
          </div>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteAllFeedbacks(student);
            }}
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2"
            title="Excluir todos os feedbacks deste aluno"
            data-testid={`delete-all-feedbacks-${student.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <div className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          </div>
        </div>
      </div>

      {/* Histórico de feedbacks expandido */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 border-t border-zinc-800/50">
              {/* Mensagens Diretas */}
              {messages.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-neon-purple" />
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">Mensagens Diretas</span>
                  </div>
                  <div className="space-y-2">
                    {messages.map((msg) => (
                      <DirectMessageItem key={msg.id} message={msg} onReply={onReplyToMessage} onDelete={onDeleteMessage} />
                    ))}
                  </div>
                </div>
              )}

              {/* Feedbacks de Treino */}
              {feedbacks.length > 0 && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-zinc-500" />
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">Feedbacks de Treino</span>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {feedbacks.map((fb) => (
                      <FeedbackItem
                        key={fb.id}
                        feedback={fb}
                        onReply={onReply}
                        onViewDetails={onViewDetails}
                        onDelete={onDeleteFeedback}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Modal de detalhes do feedback (conversa)
const FeedbackDetailModal = ({ feedback, onClose, onRefresh }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [detailFeedback, setDetailFeedback] = useState(feedback);

  const loadDetails = async () => {
    try {
      const res = await axios.get(`${API}/admin/feedbacks/${feedback.id}`);
      setDetailFeedback(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  React.useEffect(() => {
    loadDetails();
  }, []);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/feedbacks/${feedback.id}/reply`, { message: replyText });
      toast.success('Resposta enviada!');
      setReplyText('');
      loadDetails();
      onRefresh();
    } catch (e) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  const replies = detailFeedback.replies || [];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neon-purple/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-neon-purple" />
            </div>
            <div>
              <h3 className="font-bold text-white">{detailFeedback.user_name}</h3>
              <p className="text-xs text-zinc-500">{detailFeedback.day_name}</p>
            </div>
          </div>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Original feedback */}
          <div className="flex justify-start">
            <div className="bg-zinc-800 rounded-lg p-3 max-w-[80%]">
              <p className="text-zinc-300 text-sm">{detailFeedback.feedback}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {detailFeedback.feedback_at && new Date(detailFeedback.feedback_at).toLocaleDateString('pt-BR', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>
          </div>

          {/* Replies */}
          {replies.map((reply) => (
            <div key={reply.id} className={`flex ${reply.author_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
              <div className={`rounded-lg p-3 max-w-[80%] ${reply.author_type === 'admin' ? 'bg-neon-purple/20' : 'bg-zinc-800'}`}>
                <p className={`text-sm ${reply.author_type === 'admin' ? 'text-neon-purple' : 'text-zinc-300'}`}>
                  {reply.message}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {reply.author_name} • {new Date(reply.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/5">
          <div className="flex gap-2">
            <Input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Digite sua resposta..."
              className="flex-1 bg-zinc-800 border-zinc-700 text-white"
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
            />
            <Button onClick={handleSendReply} disabled={sending || !replyText.trim()} className="bg-neon-purple text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Modal de resposta rápida
const QuickReplyModal = ({ feedback, onClose, onRefresh }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/feedbacks/${feedback.id}/reply`, { message: replyText });
      toast.success('Resposta enviada!');
      onClose();
      onRefresh();
    } catch (e) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white">Responder a {feedback.user_name}</h3>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></Button>
        </div>

        <div className="bg-zinc-800 rounded-lg p-3 mb-4">
          <p className="text-zinc-300 text-sm">{feedback.feedback}</p>
          <p className="text-xs text-zinc-500 mt-1">{feedback.day_name}</p>
        </div>

        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Digite sua resposta..."
          className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg p-3 min-h-[100px] mb-4"
        />

        <div className="flex gap-3">
          <Button onClick={handleSendReply} disabled={sending || !replyText.trim()} className="flex-1 bg-neon-purple text-white">
            <Send className="w-4 h-4 mr-2" /> Enviar Resposta
          </Button>
          <Button onClick={onClose} className="bg-zinc-800 text-white">Cancelar</Button>
        </div>
      </motion.div>
    </div>
  );
};

// Modal de confirmação de exclusão
const DeleteConfirmModal = ({ title, message, onConfirm, onCancel, isDeleting }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-zinc-900 border border-red-500/30 rounded-2xl p-6 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="font-bold text-white text-lg">{title}</h3>
        </div>
        
        <p className="text-zinc-300 mb-6">{message}</p>
        
        <div className="flex gap-3">
          <Button 
            onClick={onConfirm} 
            disabled={isDeleting}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            data-testid="confirm-delete-btn"
          >
            {isDeleting ? 'Excluindo...' : 'Sim, Excluir'}
          </Button>
          <Button 
            onClick={onCancel} 
            disabled={isDeleting}
            className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700"
            data-testid="cancel-delete-btn"
          >
            Cancelar
          </Button>
        </div>
      </motion.div>
    </div>
  );
};


// Modal para admin enviar mensagem a um ou todos os alunos
const AdminSendMessageModal = ({ allUsers, onClose, onRefresh }) => {
  const [sendTo, setSendTo] = useState('all'); // 'all' or user id
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const students = allUsers.filter(u => u.role !== 'admin');
  const filteredStudents = students.filter(u =>
    !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const selectedStudent = sendTo !== 'all' ? students.find(u => u.id === sendTo) : null;

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Preencha o assunto e a mensagem');
      return;
    }
    setSending(true);
    try {
      const payload = {
        subject: subject.trim(),
        message: message.trim(),
      };
      if (sendTo !== 'all') payload.user_id = sendTo;

      const res = await axios.post(`${API}/admin/messages/send`, payload);
      const count = res.data.sent_count || 0;
      toast.success(sendTo === 'all' ? `Mensagem enviada para ${count} aluno(s)!` : 'Mensagem enviada!');
      onClose();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-zinc-900 border border-neon-lime/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-white text-lg">Enviar Mensagem</h3>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></Button>
        </div>

        {/* Destinatário */}
        <div className="mb-4">
          <label className="text-sm text-zinc-400 mb-2 block">Para quem?</label>
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSendTo('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sendTo === 'all' ? 'bg-neon-lime text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              data-testid="send-to-all-btn"
            >
              <Users className="w-4 h-4" /> Todos os alunos
            </button>
            <button
              onClick={() => setSendTo('pick')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sendTo !== 'all' ? 'bg-neon-purple text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              data-testid="send-to-one-btn"
            >
              <User className="w-4 h-4" /> Aluno específico
            </button>
          </div>

          {sendTo !== 'all' && (
            <div>
              {selectedStudent ? (
                <div className="flex items-center justify-between bg-neon-purple/10 border border-neon-purple/30 rounded-lg p-3">
                  <div>
                    <p className="font-bold text-white text-sm">{selectedStudent.name}</p>
                    <p className="text-xs text-zinc-500">{selectedStudent.email}</p>
                  </div>
                  <Button onClick={() => setSendTo('pick')} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Buscar aluno..."
                    className="bg-zinc-950 border-zinc-800 text-white text-sm mb-2"
                    data-testid="user-search-input"
                  />
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {filteredStudents.map(u => (
                      <button
                        key={u.id}
                        onClick={() => { setSendTo(u.id); setUserSearch(''); }}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 transition-colors text-left"
                      >
                        <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center text-xs text-white font-bold">
                          {u.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm text-white">{u.name}</p>
                          <p className="text-xs text-zinc-500">{u.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Assunto */}
        <div className="mb-4">
          <label className="text-sm text-zinc-400 mb-1 block">Assunto</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ex: Atualização do treino, Aviso importante..."
            className="bg-zinc-950 border-zinc-800 text-white"
            data-testid="admin-msg-subject"
          />
        </div>

        {/* Mensagem */}
        <div className="mb-5">
          <label className="text-sm text-zinc-400 mb-1 block">Mensagem</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Escreva sua mensagem..."
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-neon-lime/50 text-white rounded-lg p-3 min-h-[120px] resize-none placeholder:text-zinc-600 text-sm focus:outline-none"
            data-testid="admin-msg-body"
          />
        </div>

        {/* Ações */}
        <div className="flex gap-3">
          <Button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !message.trim() || (sendTo === 'pick')}
            className="flex-1 bg-neon-lime text-black font-bold hover:bg-neon-lime/80 disabled:opacity-50"
            data-testid="admin-send-btn"
          >
            {sending ? 'Enviando...' : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {sendTo === 'all' ? `Enviar para ${students.length} alunos` : 'Enviar'}
              </>
            )}
          </Button>
          <Button onClick={onClose} className="bg-zinc-800 text-white hover:bg-zinc-700">
            Cancelar
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Componente principal
export const FeedbacksManager = ({ feedbacks, directMessages = [], onRefresh, allUsers = [] }) => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  
  // Estados para exclusão
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [studentToDeleteAll, setStudentToDeleteAll] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Função para excluir um feedback individual
  const handleDeleteFeedback = async () => {
    if (!feedbackToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/admin/feedbacks/${feedbackToDelete.id}`);
      toast.success('Feedback excluído com sucesso!');
      setFeedbackToDelete(null);
      onRefresh();
    } catch (e) {
      toast.error('Erro ao excluir feedback');
      console.error(e);
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para excluir todos os feedbacks de um aluno
  const handleDeleteAllFeedbacks = async () => {
    if (!studentToDeleteAll) return;
    
    setIsDeleting(true);
    try {
      const res = await axios.delete(`${API}/admin/users/${studentToDeleteAll.id}/feedbacks`);
      toast.success(res.data.message || 'Todos os feedbacks foram excluídos!');
      setStudentToDeleteAll(null);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir feedbacks');
      console.error(e);
    } finally {
      setIsDeleting(false);
    }
  };

  // Agrupar feedbacks por aluno
  const groupedByStudent = useMemo(() => {
    const groups = {};
    
    feedbacks.forEach(fb => {
      const key = fb.user_id;
      if (!groups[key]) {
        groups[key] = {
          student: {
            id: fb.user_id,
            name: fb.user_name,
            email: fb.user_email
          },
          feedbacks: [],
          messages: []
        };
      }
      groups[key].feedbacks.push(fb);
    });

    // Add direct messages to groups
    directMessages.forEach(msg => {
      const key = msg.user_id;
      if (!groups[key]) {
        groups[key] = {
          student: {
            id: msg.user_id,
            name: msg.user_name,
            email: msg.user_email
          },
          feedbacks: [],
          messages: []
        };
      }
      groups[key].messages.push(msg);
    });

    // Ordenar feedbacks de cada aluno por data (mais recente primeiro)
    Object.values(groups).forEach(group => {
      group.feedbacks.sort((a, b) => new Date(b.feedback_at) - new Date(a.feedback_at));
      group.messages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    // Converter para array e ordenar alunos por data da interação mais recente
    return Object.values(groups).sort((a, b) => {
      const dateA = Math.max(
        a.feedbacks[0]?.feedback_at ? new Date(a.feedbacks[0].feedback_at).getTime() : 0,
        a.messages[0]?.created_at ? new Date(a.messages[0].created_at).getTime() : 0
      );
      const dateB = Math.max(
        b.feedbacks[0]?.feedback_at ? new Date(b.feedbacks[0].feedback_at).getTime() : 0,
        b.messages[0]?.created_at ? new Date(b.messages[0].created_at).getTime() : 0
      );
      return dateB - dateA;
    });
  }, [feedbacks, directMessages]);

  // Filtrar alunos por busca e status de não lido
  const filteredGroups = useMemo(() => {
    return groupedByStudent.filter(group => {
      const matchSearch = !searchTerm || 
        group.student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.student.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const hasUnreadFb = group.feedbacks.some(f => f.has_unread_reply);
      const hasUnreadMsg = group.messages.some(m => m.has_unread_reply || !m.read_by_admin);
      const matchUnread = !filterUnread || hasUnreadFb || hasUnreadMsg;

      return matchSearch && matchUnread;
    });
  }, [groupedByStudent, searchTerm, filterUnread]);

  const toggleStudent = (studentId) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
        // Mark messages as read when expanding
        axios.post(`${API}/admin/messages/read/${studentId}`).then(() => {
          onRefresh();
        }).catch(() => {});
      }
      return newSet;
    });
  };

  const totalUnread = feedbacks.filter(f => f.has_unread_reply).length + 
    directMessages.filter(m => m.has_unread_reply || !m.read_by_admin).length;

  const handleReplyToMessage = async (message, replyText) => {
    try {
      await axios.post(`${API}/admin/messages/${message.id}/reply`, { message: replyText });
      toast.success('Resposta enviada!');
      onRefresh();
    } catch (e) {
      toast.error('Erro ao enviar resposta');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`);
      toast.success('Mensagem excluída!');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir mensagem');
    }
  };


  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6" data-testid="feedbacks-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-neon-purple" />
          <div>
            <h3 className="font-primary font-bold text-xl text-white">Feedbacks e Mensagens</h3>
            <p className="text-zinc-500 text-sm">
              Histórico organizado por aluno • {groupedByStudent.length} aluno(s) • {feedbacks.length} feedback(s) • {directMessages.length} mensagem(ns)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {totalUnread > 0 && (
            <span className="bg-neon-purple text-white text-sm px-3 py-1 rounded-full font-bold">
              {totalUnread} não lido(s)
            </span>
          )}
          <Button
            onClick={() => setShowSendModal(true)}
            className="bg-neon-lime text-black font-bold hover:bg-neon-lime/80"
            data-testid="admin-send-message-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Enviar Mensagem
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por aluno..."
            className="pl-10 bg-zinc-950/50 border-zinc-800 text-white h-10"
            data-testid="search-feedback-input"
          />
        </div>
        <Button
          onClick={() => setFilterUnread(!filterUnread)}
          variant={filterUnread ? "default" : "outline"}
          className={filterUnread ? "bg-neon-purple text-white" : "border-zinc-700 text-zinc-400"}
          data-testid="filter-unread-btn"
        >
          <Filter className="w-4 h-4 mr-2" />
          Não lidos
        </Button>
      </div>

      {/* Lista de alunos com feedbacks */}
      {filteredGroups.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">
            {feedbacks.length === 0 
              ? 'Nenhum feedback recebido ainda' 
              : 'Nenhum aluno encontrado com os filtros aplicados'}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="feedbacks-list">
          {filteredGroups.map((group) => (
            <StudentFeedbackCard
              key={group.student.id}
              student={group.student}
              feedbacks={group.feedbacks}
              messages={group.messages}
              onReply={(f) => setReplyingTo(f)}
              onViewDetails={(f) => setSelectedFeedback(f)}
              onDeleteFeedback={(f) => setFeedbackToDelete(f)}
              onDeleteAllFeedbacks={(s) => setStudentToDeleteAll(s)}
              onReplyToMessage={handleReplyToMessage}
              onDeleteMessage={handleDeleteMessage}
              isExpanded={expandedStudents.has(group.student.id)}
              onToggle={() => toggleStudent(group.student.id)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {selectedFeedback && (
          <FeedbackDetailModal
            feedback={selectedFeedback}
            onClose={() => setSelectedFeedback(null)}
            onRefresh={onRefresh}
          />
        )}
        {replyingTo && (
          <QuickReplyModal
            feedback={replyingTo}
            onClose={() => setReplyingTo(null)}
            onRefresh={onRefresh}
          />
        )}
        {feedbackToDelete && (
          <DeleteConfirmModal
            title="Excluir Feedback"
            message={`Tem certeza que deseja excluir este feedback de "${feedbackToDelete.user_name}"? Esta ação não pode ser desfeita.`}
            onConfirm={handleDeleteFeedback}
            onCancel={() => setFeedbackToDelete(null)}
            isDeleting={isDeleting}
          />
        )}
        {studentToDeleteAll && (
          <DeleteConfirmModal
            title="Excluir Todos os Feedbacks"
            message={`Tem certeza que deseja excluir TODOS os feedbacks de "${studentToDeleteAll.name}"? Esta ação é irreversível e apagará todo o histórico de conversas.`}
            onConfirm={handleDeleteAllFeedbacks}
            onCancel={() => setStudentToDeleteAll(null)}
            isDeleting={isDeleting}
          />
        )}
        {showSendModal && (
          <AdminSendMessageModal
            allUsers={allUsers}
            onClose={() => setShowSendModal(false)}
            onRefresh={onRefresh}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
